import React, { useState, useEffect, useCallback } from 'react';
import { ageGroupsAPI, authAPI, playersAPI } from '../../services/api';
import { AgeGroupPlayer } from '../../types/ageGroup';
import { Player } from '../../types/player';
import {
  XMarkIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import PlayerAgeGroupConfirmModal from './PlayerAgeGroupConfirmModal';
import Modal from '../ui/Modal';
import { matchesSearch as matchesSearchText } from '../../utils/search';
import { useToast } from '../../contexts/ToastContext';

/** Kereséskor ennyi hozzáadható (más korosztályú) játékos jelenik meg. */
const MAX_ADDABLE = 30;

interface AgeGroupDetailProps {
  ageGroupId: number;
  ageGroupName: string;
  /** A korosztály születési dátum-tartománya (ISO) — az ide nem illő játékosok jelöléséhez */
  birthDateFrom?: string;
  birthDateTo?: string;
  onUpdate: () => void;
}

type TabType = 'players' | 'coaches';

const toAgeGroupPlayer = (player: Player): AgeGroupPlayer => ({
  id: player.id,
  name: player.name,
  birth: player.birth,
  isAutoSuggested: false,
  currentAgeGroup: player.ageGroup || undefined,
  ageGroupIds: player.ageGroupId ? [player.ageGroupId] : [],
});

const AgeGroupDetail: React.FC<AgeGroupDetailProps> = ({
  ageGroupId,
  ageGroupName,
  birthDateFrom,
  birthDateTo,
  onUpdate,
}) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('players');
  const [players, setPlayers] = useState<AgeGroupPlayer[]>([]);
  const [suggestedPlayers, setSuggestedPlayers] = useState<AgeGroupPlayer[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [allCoaches, setAllCoaches] = useState<any[]>([]);
  const [selectedCoachIds, setSelectedCoachIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingCoaches, setSavingCoaches] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<AgeGroupPlayer | null>(null);
  const [busyPlayerId, setBusyPlayerId] = useState<number | null>(null);
  // Tömeges hozzáadás: a kijelölt játékosokat objektumként tartjuk, hogy a
  // keresés szűkítése ne dobja el a korábbi kijelöléseket
  const [selected, setSelected] = useState<AgeGroupPlayer[]>([]);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  // A fejléc összefoglalója mindkét fül adatait mutatja, ezért egyszerre töltünk be mindent
  const loadData = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const [playersData, suggestedData, allPlayersData, coachesData, allCoachesData] = await Promise.all([
        ageGroupsAPI.getPlayers(ageGroupId),
        ageGroupsAPI.getSuggestedPlayers(ageGroupId),
        playersAPI.getAll(),
        ageGroupsAPI.getCoaches(ageGroupId),
        authAPI.getCoaches(),
      ]);
      setPlayers(playersData);
      setSuggestedPlayers(suggestedData);
      setAllPlayers(allPlayersData);
      // A már bekerült játékosok kikerülnek a kijelölésből
      setSelected((prev) => prev.filter((p) => !playersData.some((pd) => pd.id === p.id)));
      setAllCoaches(allCoachesData);
      setSelectedCoachIds(coachesData.map((c) => c.userId));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [ageGroupId]);

  useEffect(() => {
    loadData(true);
  }, [loadData]);

  /** Hozzáadás egy lépésben: ha a játékos nincs más korosztályban, azonnal
      hozzáadjuk; csak ütközés esetén nyílik a megerősítő modal. */
  const startAddPlayer = async (player: AgeGroupPlayer) => {
    setBusyPlayerId(player.id);
    try {
      const result = await ageGroupsAPI.getPlayerAgeGroups(player.id);
      const others = result.ageGroups.filter((ag) => ag.id !== ageGroupId);
      if (others.length === 0) {
        await ageGroupsAPI.addPlayer(ageGroupId, player.id, []);
        showToast(`${player.name} hozzáadva a korosztályhoz`);
        loadData();
        onUpdate();
      } else {
        setSelectedPlayer(player);
        setConfirmModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to add player:', error);
      showToast('Hiba történt a hozzáadáskor', 'error');
    } finally {
      setBusyPlayerId(null);
    }
  };

  const handleConfirmAddPlayer = async (removeFromAgeGroupIds: number[]) => {
    if (!selectedPlayer) return;
    try {
      await ageGroupsAPI.addPlayer(ageGroupId, selectedPlayer.id, removeFromAgeGroupIds);
      showToast(`${selectedPlayer.name} hozzáadva a korosztályhoz`);
      loadData();
      onUpdate();
    } catch (error) {
      console.error('Failed to add player:', error);
      showToast('Hiba történt a hozzáadáskor', 'error');
    } finally {
      setConfirmModalOpen(false);
      setSelectedPlayer(null);
    }
  };

  /** Igaz, ha a játékos más korosztály(ok)nak is tagja */
  const hasOtherAgeGroup = (player: AgeGroupPlayer): boolean => {
    if (player.ageGroupIds.some((id) => id !== ageGroupId)) return true;
    return (player.currentAgeGroup || '')
      .split(',')
      .map((name) => name.trim())
      .some((name) => name !== '' && name !== ageGroupName && name !== 'Nincs besorolva');
  };

  const isSelected = (playerId: number) => selected.some((p) => p.id === playerId);

  const toggleSelect = (player: AgeGroupPlayer) => {
    setSelected((prev) =>
      prev.some((p) => p.id === player.id)
        ? prev.filter((p) => p.id !== player.id)
        : [...prev, player],
    );
  };

  /** Szakasz összes elemének kijelölése, vagy a kijelölés visszavonása, ha már mind ki van jelölve */
  const toggleSelectSection = (sectionPlayers: AgeGroupPlayer[]) => {
    const allSelected = sectionPlayers.every((p) => isSelected(p.id));
    setSelected((prev) =>
      allSelected
        ? prev.filter((p) => !sectionPlayers.some((sp) => sp.id === p.id))
        : [...prev, ...sectionPlayers.filter((p) => !prev.some((x) => x.id === p.id))],
    );
  };

  /** Tömeges hozzáadás: ütközés esetén egyszer kérdezünk rá a korábbi korosztályokra */
  const startBulkAdd = () => {
    if (selected.length === 0) return;
    if (selected.some(hasOtherAgeGroup)) {
      setBulkModalOpen(true);
    } else {
      runBulkAdd(false);
    }
  };

  const runBulkAdd = async (removeFromOtherAgeGroups: boolean) => {
    setBulkSaving(true);
    try {
      const result = await ageGroupsAPI.addPlayers(
        ageGroupId,
        selected.map((p) => p.id),
        removeFromOtherAgeGroups,
      );
      showToast(`${result.addedCount} játékos hozzáadva a korosztályhoz`);
      setSelected([]);
      loadData();
      onUpdate();
    } catch (error) {
      console.error('Failed to add players:', error);
      showToast('Hiba történt a hozzáadáskor', 'error');
    } finally {
      setBulkSaving(false);
      setBulkModalOpen(false);
    }
  };

  const handleRemovePlayer = async (player: AgeGroupPlayer) => {
    try {
      await ageGroupsAPI.removePlayer(ageGroupId, player.id);
      showToast(`${player.name} eltávolítva a korosztályból`);
      loadData();
      onUpdate();
    } catch (error) {
      console.error('Failed to remove player:', error);
      showToast('Hiba történt az eltávolításkor', 'error');
    }
  };

  /** Edzők: automatikus mentés minden pipálásnál, hiba esetén visszaállítás. */
  const handleCoachToggle = async (coachId: number) => {
    const prev = selectedCoachIds;
    const next = prev.includes(coachId)
      ? prev.filter((id) => id !== coachId)
      : [...prev, coachId];
    setSelectedCoachIds(next);
    setSavingCoaches(true);
    try {
      await ageGroupsAPI.updateCoaches(ageGroupId, { userIds: next });
      onUpdate();
    } catch (error) {
      console.error('Failed to save coaches:', error);
      setSelectedCoachIds(prev);
      showToast('Hiba történt az edzők mentésekor', 'error');
    } finally {
      setSavingCoaches(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  /** Igaz, ha a játékos születési ideje a korosztály tartományán kívül esik.
      ISO dátum-stringek napra vágva hasonlíthatók lexikografikusan. */
  const isOutOfRange = (birth: string): boolean => {
    if (!birthDateFrom || !birthDateTo) return false;
    const b = birth.slice(0, 10);
    return b < birthDateFrom.slice(0, 10) || b > birthDateTo.slice(0, 10);
  };

  const outOfRangeCount = players.filter((p) => isOutOfRange(p.birth)).length;

  const normalizedSearch = search.trim();
  const matchesSearch = (name: string) => matchesSearchText(search, name);

  const currentPlayerIds = new Set(players.map((p) => p.id));
  const suggestedIds = new Set(suggestedPlayers.map((p) => p.id));
  const filteredCurrent = players.filter((p) => matchesSearch(p.name));
  const filteredSuggested = suggestedPlayers.filter((p) => matchesSearch(p.name));
  // Kereséskor a többi (nem tag, nem javasolt) játékos is hozzáadható találatként jelenik meg
  const allAddable = normalizedSearch === ''
    ? []
    : allPlayers.filter(
        (p) => !currentPlayerIds.has(p.id) && !suggestedIds.has(p.id) && matchesSearch(p.name)
      );
  // Egy-két betűre az egész klub illene - a lista rövid marad, a többihez tovább kell gépelni
  const addablePlayers = allAddable.slice(0, MAX_ADDABLE).map(toAgeGroupPlayer);
  const hiddenAddableCount = allAddable.length - addablePlayers.length;

  const tabs = [
    { id: 'players' as TabType, name: `Játékosok (${players.length})` },
    { id: 'coaches' as TabType, name: `Edzők (${selectedCoachIds.length})` },
  ];

  const renderAddButton = (player: AgeGroupPlayer) => (
    <button
      onClick={() => startAddPlayer(player)}
      disabled={busyPlayerId !== null}
      className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
    >
      <UserPlusIcon className="h-4 w-4" />
      {busyPlayerId === player.id ? '...' : 'Hozzáad'}
    </button>
  );

  /** Kijelölhető sor: pipa a tömeges hozzáadáshoz + egyedi "Hozzáad" gomb */
  const renderSelectableRow = (player: AgeGroupPlayer) => (
    <div key={player.id} className="flex items-center gap-2 px-3 py-2">
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          checked={isSelected(player.id)}
          onChange={() => toggleSelect(player)}
          className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="min-w-0">
          <span className="block break-words text-sm font-medium leading-snug text-gray-900">
            {player.name}
          </span>
          <span className="block text-xs text-gray-500">
            Született: {formatDate(player.birth)}
            {player.currentAgeGroup && (
              <span className="ml-2 text-orange-600">(jelenlegi: {player.currentAgeGroup})</span>
            )}
          </span>
        </span>
      </label>
      {renderAddButton(player)}
    </div>
  );

  /** Szakasz-fejléc a "mind kijelölése" kapcsolóval */
  const renderSectionHeader = (
    title: string,
    sectionPlayers: AgeGroupPlayer[],
    subtitle?: string,
  ) => {
    const allSelected = sectionPlayers.every((p) => isSelected(p.id));
    return (
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-gray-700">
          {title} ({sectionPlayers.length})
          {subtitle && <span className="ml-2 text-xs font-normal text-gray-500">{subtitle}</span>}
        </h4>
        <button
          type="button"
          onClick={() => toggleSelectSection(sectionPlayers)}
          className="shrink-0 text-xs font-medium text-primary-600 hover:text-primary-700"
        >
          {allSelected ? 'Kijelölés törlése' : 'Mind kijelölése'}
        </button>
      </div>
    );
  };

  const assignedCoaches = allCoaches.filter((c) => selectedCoachIds.includes(c.id));

  /** "2014" vagy "2013–2014" */
  const birthYears = () => {
    if (!birthDateFrom || !birthDateTo) return '–';
    const from = new Date(birthDateFrom).getFullYear();
    const to = new Date(birthDateTo).getFullYear();
    return from === to ? String(from) : `${from}–${to}`;
  };

  const summary = [
    {
      label: 'Játékosok',
      value: String(players.length),
      icon: UsersIcon,
      tone: 'text-primary-700',
    },
    {
      label: 'Születési idő',
      value: birthYears(),
      detail: birthDateFrom && birthDateTo ? `${formatDate(birthDateFrom)} – ${formatDate(birthDateTo)}` : undefined,
      icon: CalendarDaysIcon,
      tone: 'text-gray-900',
    },
    {
      label: 'Edzők',
      value: String(assignedCoaches.length),
      detail: assignedCoaches.length > 0 ? assignedCoaches.map((c) => c.name).join(', ') : 'Nincs edző hozzárendelve',
      icon: AcademicCapIcon,
      tone: assignedCoaches.length > 0 ? 'text-gray-900' : 'text-amber-700',
    },
    outOfRangeCount > 0
      ? {
          label: 'Nem korosztálybeli',
          value: String(outOfRangeCount),
          detail: 'születési idő alapján',
          icon: ExclamationTriangleIcon,
          tone: 'text-amber-700',
        }
      : {
          label: 'Javasolt',
          value: String(suggestedPlayers.length),
          detail: 'születési dátum alapján',
          icon: SparklesIcon,
          tone: suggestedPlayers.length > 0 ? 'text-green-700' : 'text-gray-900',
        },
  ];

  return (
    <div>
      {/* Összefoglaló: a korosztály legfontosabb adatai egy pillantásra */}
      {!loading && (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {summary.map((item) => (
            <div key={item.label} className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                <item.icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </div>
              <p className={`mt-1 text-2xl font-bold ${item.tone}`}>{item.value}</p>
              {item.detail && (
                <p className="truncate text-xs text-gray-500" title={item.detail}>
                  {item.detail}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <p className="text-gray-500">Betöltés...</p>
        </div>
      ) : (
        <>
          {/* Players Tab */}
          {activeTab === 'players' && (
            <div className="space-y-5">
              {/* Kereső: szűr a tagok közt és hozzáadható játékosokat is talál */}
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Játékos keresése vagy hozzáadása..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="block w-full rounded-md border-0 py-2 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm"
                />
              </div>

              {/* Figyelmeztetés a korosztályba nem illő játékosokra */}
              {outOfRangeCount > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-500" />
                  <p className="text-sm text-amber-800">
                    {outOfRangeCount} játékos születési idő alapján nem ebbe a korosztályba
                    tartozik ({birthDateFrom && formatDate(birthDateFrom)} –{' '}
                    {birthDateTo && formatDate(birthDateTo)}).
                  </p>
                </div>
              )}

              {/* Jelenlegi játékosok */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Jelenlegi játékosok ({players.length})
                </h4>
                {filteredCurrent.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    {players.length === 0
                      ? 'Nincs játékos ebben a korosztályban.'
                      : 'Nincs a keresésre illeszkedő játékos.'}
                  </p>
                ) : (
                  <div className="grid gap-px overflow-hidden rounded-md border border-gray-200 bg-gray-200 md:grid-cols-2 md:[&>*:last-child:nth-child(odd)]:col-span-2">
                    {filteredCurrent.map((player) => (
                      <div
                        key={player.id}
                        className={`flex items-center justify-between px-3 py-2 ${
                          isOutOfRange(player.birth) ? 'bg-amber-50' : 'bg-white'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 truncate text-sm font-medium text-gray-900">
                            {player.name}
                            {isOutOfRange(player.birth) && (
                              <span
                                className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                                title="A születési idő a korosztály tartományán kívül esik"
                              >
                                <ExclamationTriangleIcon className="h-3 w-3" />
                                nem korosztálybeli
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">
                            Született: {formatDate(player.birth)}
                            {player.isAutoSuggested && (
                              <span className="ml-2 text-green-600">(dátum alapján)</span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemovePlayer(player)}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600"
                          title="Eltávolítás a korosztályból"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Kereséskor: hozzáadható játékosok */}
              {normalizedSearch !== '' && addablePlayers.length > 0 && (
                <div>
                  {renderSectionHeader('Hozzáadható játékosok', addablePlayers)}
                  <div className="grid gap-px overflow-hidden rounded-md border border-gray-200 bg-gray-200 md:grid-cols-2 md:[&>*:last-child:nth-child(odd)]:col-span-2 [&>*]:bg-white">
                    {addablePlayers.map(renderSelectableRow)}
                  </div>
                  {hiddenAddableCount > 0 && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      {hiddenAddableCount} további találat - gépelj tovább a szűkítéshez.
                    </p>
                  )}
                </div>
              )}

              {/* Javasolt játékosok */}
              {filteredSuggested.length > 0 && (
                <div>
                  {renderSectionHeader(
                    'Javasolt játékosok',
                    filteredSuggested,
                    '(születési dátum alapján)',
                  )}
                  <div className="grid gap-px overflow-hidden rounded-md border border-green-200 bg-green-200 md:grid-cols-2 md:[&>*:last-child:nth-child(odd)]:col-span-2 [&>*]:bg-green-50">
                    {filteredSuggested.map(renderSelectableRow)}
                  </div>
                </div>
              )}

              {/* Tömeges hozzáadás sávja — a panel aljára tapad, ha van kijelölés */}
              {selected.length > 0 && (
                <div className="sticky bottom-0 -mx-4 flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {selected.length} játékos kijelölve
                    </p>
                    <button
                      type="button"
                      onClick={() => setSelected([])}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Kijelölés törlése
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={startBulkAdd}
                    disabled={bulkSaving}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    <UserPlusIcon className="h-5 w-5" />
                    {bulkSaving ? 'Hozzáadás...' : 'Hozzáadás'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tömeges hozzáadás megerősítése — csak ha van más korosztályban lévő játékos */}
          <Modal
            isOpen={bulkModalOpen}
            onClose={() => setBulkModalOpen(false)}
            title={`${selected.length} játékos hozzáadása`}
          >
            <div className="space-y-4 pb-4">
              <p className="text-sm text-gray-600">
                A kijelöltek közül {selected.filter(hasOtherAgeGroup).length} játékos már más
                korosztályban is szerepel:
              </p>
              <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                {selected.filter(hasOtherAgeGroup).map((player) => (
                  <li key={player.id} className="break-words hyphens-auto leading-snug">
                    {player.name}
                    {player.currentAgeGroup && (
                      <span className="text-gray-500"> — {player.currentAgeGroup}</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-600">Töröljük őket a korábbi korosztályokból?</p>
              <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-end md:gap-3">
                <button
                  type="button"
                  onClick={() => setBulkModalOpen(false)}
                  disabled={bulkSaving}
                  className="w-full rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 md:w-auto"
                >
                  Mégse
                </button>
                <button
                  type="button"
                  onClick={() => runBulkAdd(false)}
                  disabled={bulkSaving}
                  className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50 md:w-auto"
                >
                  Nem, maradjon mindben
                </button>
                <button
                  type="button"
                  onClick={() => runBulkAdd(true)}
                  disabled={bulkSaving}
                  className="w-full rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50 md:w-auto"
                >
                  Igen, törlés
                </button>
              </div>
            </div>
          </Modal>

          {/* Player Confirmation Modal (csak ütközés esetén nyílik) */}
          <PlayerAgeGroupConfirmModal
            isOpen={confirmModalOpen}
            onClose={() => {
              setConfirmModalOpen(false);
              setSelectedPlayer(null);
            }}
            player={selectedPlayer}
            targetAgeGroupId={ageGroupId}
            targetAgeGroupName={ageGroupName}
            onConfirm={handleConfirmAddPlayer}
          />

          {/* Coaches Tab */}
          {activeTab === 'coaches' && (
            <div className="space-y-4">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700">Edzők hozzárendelése</h4>
                  {savingCoaches && <span className="text-xs text-gray-400">Mentés...</span>}
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Jelöld ki, mely edzők férhetnek hozzá ehhez a korosztályhoz — a módosítás
                  automatikusan mentődik.
                </p>
                {allCoaches.length === 0 ? (
                  <p className="text-sm text-gray-500">Nincsenek elérhető edzők.</p>
                ) : (
                  <div className="grid gap-px overflow-hidden rounded-md border border-gray-200 bg-gray-200 md:grid-cols-2 md:[&>*:last-child:nth-child(odd)]:col-span-2">
                    {allCoaches.map((coach) => (
                      <label
                        key={coach.id}
                        className="flex items-center bg-white px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCoachIds.includes(coach.id)}
                          onChange={() => handleCoachToggle(coach.id)}
                          disabled={savingCoaches}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                        />
                        <div className="ml-3 min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">{coach.name}</p>
                          <p className="truncate text-xs text-gray-500">{coach.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgeGroupDetail;
